import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Badge, Button } from 'react-bootstrap'
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.js'
import { PrintArea } from './PrintArea'
import {
  doesPdfPlacementIntersectPage,
  getPdfPageMetadata,
  isPdfPlacement,
  isPdfPlacementCompatible,
  pdfPlacementToViewportRect,
  viewportRectToPdfPlacement,
} from './pdfOverlayCoordinate.util'
import {
  getAttachmentFileUrl,
  getAttachmentId,
} from './resourceUpload'
import {
  needsPdfMeasurement,
  mergePdfMeasurement,
} from './pdfOverlayMeasurement.util'
import './PdfOverlayDesigner.scss'

pdfjsLib.GlobalWorkerOptions.workerSrc =
  '/his/libs/pdfjs/2.10.377/pdf.worker.min.js'

const PDF_RENDER_SCALE = 1
const TEXT_ITEM_TYPES = ['text', 'object', 'search_param', 'texteditor']
const UNSUPPORTED_TINYMCE_TAG_PATTERN = /<(table|img|svg|canvas|video|iframe)\b/i

const getPdfTemplateItem = (boxes = {}) => {
  const pdfItems = Object.values(boxes || {}).filter(
    (item) => item?.type === 'pdf',
  )

  return (
    pdfItems.find(
      (item) => getAttachmentId(item) || getAttachmentId(item?.pdf),
    ) || pdfItems[0]
  )
}

const getPdfTemplateSource = (item = {}) =>
  item.previewUrl ||
  item?.pdf?.previewUrl ||
  getAttachmentFileUrl(getAttachmentId(item) || getAttachmentId(item?.pdf))

const getOverlayBoxes = (boxes = {}) =>
  Object.keys(boxes || {}).reduce((result, key) => {
    if (boxes[key]?.type !== 'pdf') {
      result[key] = boxes[key]
    }
    return result
  }, {})

const roundNumber = (value) => Math.round(Number(value || 0) * 10000) / 10000

const roundPlacement = (placement) => ({
  ...placement,
  sourcePageView: placement.sourcePageView.map(roundNumber),
  quad: Object.keys(placement.quad).reduce((result, key) => {
    result[key] = placement.quad[key].map(roundNumber)
    return result
  }, {}),
})

const getSha256 = async (data) => {
  if (!window.crypto?.subtle) return ''

  const digest = await window.crypto.subtle.digest('SHA-256', data)
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

const getMeasuredTextStyle = (node, item, layout) => {
  if (!TEXT_ITEM_TYPES.includes(item?.type)) return undefined

  const style = window.getComputedStyle(node)
  return {
    fontFamily: item?.fontFamily || layout?.fontFamily || 'THSarabunNew',
    fontSizePt: roundNumber(parseFloat(style.fontSize) || 14),
    lineHeightPt: roundNumber(
      parseFloat(style.lineHeight) || (parseFloat(style.fontSize) || 14) * 1.15,
    ),
    textAlign: style.textAlign || item?.textAlign || 'left',
    fontWeight: parseInt(style.fontWeight, 10) || 400,
    fontStyle: style.fontStyle || item?.fontStyle || 'normal',
    color: item?.textColor || style.color || '#000000',
    verticalAlign: item?.verticalAlign || 'top',
    paddingPt: roundNumber(parseFloat(style.paddingLeft) || 0),
  }
}

export const PdfOverlayDesigner = ({
  boxes = {},
  pdfTemplateBoxes = boxes,
  layout = {},
  page = 1,
  onPdfGeometryChange,
  onPdfTemplateChange,
  ...printAreaProps
}) => {
  const rootRef = useRef(null)
  const canvasRef = useRef(null)
  const geometryChangeRef = useRef(onPdfGeometryChange)
  const templateChangeRef = useRef(onPdfTemplateChange)
  const [pdfDocument, setPdfDocument] = useState(null)
  const [pageView, setPageView] = useState(null)
  const [templateMetadata, setTemplateMetadata] = useState(null)
  const [status, setStatus] = useState('idle')
  const [errorMessage, setErrorMessage] = useState('')

  geometryChangeRef.current = onPdfGeometryChange
  templateChangeRef.current = onPdfTemplateChange

  const templateItem = useMemo(
    () => getPdfTemplateItem(pdfTemplateBoxes),
    [pdfTemplateBoxes],
  )
  const attachmentId =
    getAttachmentId(templateItem) || getAttachmentId(templateItem?.pdf)
  const templateSource = getPdfTemplateSource(templateItem)
  const overlayBoxes = useMemo(() => getOverlayBoxes(boxes), [boxes])
  const richTextWarningCount = useMemo(
    () =>
      Object.values(overlayBoxes).filter(
        (item) =>
          item?.type === 'texteditor' &&
          UNSUPPORTED_TINYMCE_TAG_PATTERN.test(item?.sourceCode || ''),
      ).length,
    [overlayBoxes],
  )

  useEffect(() => {
    if (!templateSource) {
      setPdfDocument(null)
      setPageView(null)
      setTemplateMetadata(null)
      setStatus('idle')
      setErrorMessage('')
      return undefined
    }

    let cancelled = false
    let loadingTask = null

    const loadPdf = async () => {
      setStatus('loading')
      setErrorMessage('')

      try {
        const response = await fetch(templateSource, {
          credentials: 'include',
        })
        if (!response.ok) {
          throw new Error('ไม่สามารถโหลดไฟล์ PDF template ได้')
        }

        const buffer = await response.arrayBuffer()
        const sha256 = await getSha256(buffer.slice(0))
        loadingTask = pdfjsLib.getDocument({ data: new Uint8Array(buffer) })
        const nextPdfDocument = await loadingTask.promise
        const pages = await Promise.all(
          Array.from({ length: nextPdfDocument.numPages }, async (_, index) => {
            const pdfPage = await nextPdfDocument.getPage(index + 1)
            return getPdfPageMetadata(pdfPage, index + 1)
          }),
        )
        const nextTemplateMetadata = {
          attachmentId,
          sha256,
          pageCount: nextPdfDocument.numPages,
          pages,
        }

        if (!cancelled) {
          setPdfDocument(nextPdfDocument)
          setTemplateMetadata(nextTemplateMetadata)
          templateChangeRef.current?.(nextTemplateMetadata)
        }
      } catch (error) {
        if (!cancelled) {
          setStatus('error')
          setErrorMessage(error?.message || 'ไม่สามารถเปิด PDF template ได้')
        }
      }
    }

    loadPdf()

    return () => {
      cancelled = true
      setPdfDocument(null)
      if (loadingTask?.destroy) {
        loadingTask.destroy()
      }
    }
  }, [attachmentId, templateSource])

  useEffect(() => {
    if (!pdfDocument) return undefined

    let cancelled = false
    let renderTask = null

    const renderPage = async () => {
      try {
        const pageNumber = Math.min(
          Math.max(Number(page) || 1, 1),
          pdfDocument.numPages,
        )
        const pdfPage = await pdfDocument.getPage(pageNumber)
        const viewport = pdfPage.getViewport({
          scale: PDF_RENDER_SCALE,
          rotation: pdfPage.rotate,
        })
        const canvas = canvasRef.current

        if (!canvas || cancelled) return

        const outputScale = window.devicePixelRatio || 1
        const context = canvas.getContext('2d')

        canvas.width = Math.floor(viewport.width * outputScale)
        canvas.height = Math.floor(viewport.height * outputScale)
        canvas.style.width = `${viewport.width}px`
        canvas.style.height = `${viewport.height}px`

        renderTask = pdfPage.render({
          canvasContext: context,
          viewport,
          transform:
            outputScale === 1
              ? null
              : [outputScale, 0, 0, outputScale, 0, 0],
        })
        await renderTask.promise

        if (!cancelled) {
          setPageView({
            ...getPdfPageMetadata(pdfPage, pageNumber),
            width: viewport.width,
            height: viewport.height,
            viewport,
          })
          setStatus('ready')
        }
      } catch (error) {
        if (!cancelled && error?.name !== 'RenderingCancelledException') {
          setStatus('error')
          setErrorMessage(error?.message || 'ไม่สามารถ render PDF template ได้')
        }
      }
    }

    renderPage()

    return () => {
      cancelled = true
      if (renderTask?.cancel) {
        renderTask.cancel()
      }
    }
  }, [pdfDocument, page])

  const incompatibleItemIds = useMemo(() => {
    if (!pageView || !templateMetadata) return []

    return Object.values(overlayBoxes)
      .filter(
        (item) =>
          isPdfPlacement(item?.pdfPlacement) &&
          !isPdfPlacementCompatible(
            item.pdfPlacement,
            pageView,
            templateMetadata.sha256,
          ),
      )
      .map((item) => String(item.id))
  }, [overlayBoxes, pageView, templateMetadata])

  const renderedBoxes = useMemo(() => {
    if (!pageView || !templateMetadata) return overlayBoxes

    return Object.keys(overlayBoxes).reduce((result, key) => {
      const item = overlayBoxes[key]
      if (
        isPdfPlacementCompatible(
          item?.pdfPlacement,
          pageView,
          templateMetadata.sha256,
        ) && doesPdfPlacementIntersectPage(item.pdfPlacement, pageView)
      ) {
        const rect = pdfPlacementToViewportRect(
          pageView.viewport,
          item.pdfPlacement,
        )
        result[key] = {
          ...item,
          left: roundNumber(rect.left),
          top: roundNumber(rect.top),
          width: roundNumber(rect.width),
          height: roundNumber(rect.height),
        }
      } else {
        result[key] = item
      }
      return result
    }, {})
  }, [overlayBoxes, pageView, templateMetadata])

  useEffect(() => {
    if (
      status !== 'ready' ||
      !pageView?.viewport ||
      !templateMetadata ||
      !geometryChangeRef.current
    ) {
      return undefined
    }

    const geometryTimer = window.setTimeout(() => {
      const pageNode = rootRef.current?.querySelector('.print-area')
      if (!pageNode) return

      const pageRect = pageNode.getBoundingClientRect()
      const nextItems = {}

      Object.values(renderedBoxes).forEach((item) => {
        if (!item?.id || incompatibleItemIds.includes(String(item.id))) return

        const sourceItem = overlayBoxes[String(item.id)] || item
        if (!needsPdfMeasurement(sourceItem)) return

        const node = pageNode.querySelector(`[data-box-id="${item.id}"]`)
        if (!node) return

        const rect = node.getBoundingClientRect()
        const left = node.style.left.endsWith('px')
          ? parseFloat(node.style.left)
          : rect.left - pageRect.left
        const top = node.style.top.endsWith('px')
          ? parseFloat(node.style.top)
          : rect.top - pageRect.top
        const cssRect = {
          left,
          top,
          width: node.style.width.endsWith('px')
            ? parseFloat(node.style.width)
            : rect.width,
          height: node.style.height.endsWith('px')
            ? parseFloat(node.style.height)
            : rect.height,
        }
        const pdfPlacement = roundPlacement(
          viewportRectToPdfPlacement(
            pageView.viewport,
            cssRect,
            pageView,
            templateMetadata.sha256,
          ),
        )
        const pdfTextStyle = getMeasuredTextStyle(node, item, layout)
        nextItems[String(item.id)] = {
          source: sourceItem,
          changes: mergePdfMeasurement(sourceItem, pdfPlacement, pdfTextStyle),
        }
      })

      if (Object.keys(nextItems).length) {
        geometryChangeRef.current(nextItems)
      }
    }, 500)

    return () => window.clearTimeout(geometryTimer)
  }, [
    incompatibleItemIds,
    layout,
    overlayBoxes,
    pageView,
    renderedBoxes,
    status,
    templateMetadata,
  ])

  const pageLayout = pageView
    ? {
        ...layout,
        width: `${pageView.width}px`,
        height: `${pageView.height}px`,
      }
    : layout

  const backgroundContent = (
    <div className="pdf-overlay-designer__background" aria-hidden="true">
      {templateSource && <canvas ref={canvasRef} />}
      {status === 'loading' && (
        <div className="pdf-overlay-designer__status">
          กำลังโหลด PDF template...
        </div>
      )}
      {status === 'error' && (
        <div className="pdf-overlay-designer__status is-error">
          {errorMessage}
        </div>
      )}
      {!templateSource && (
        <div className="pdf-overlay-designer__empty">
          <i className="far fa-file-pdf" />
          <strong>ยังไม่มี PDF template</strong>
          <span>ลาก PDF จาก Resource มาวางบนพื้นที่นี้เพื่อเริ่ม POC</span>
        </div>
      )}
    </div>
  )

  return (
    <section className="pdf-overlay-designer" ref={rootRef}>
      <div className="pdf-overlay-designer__toolbar">
        <div>
          <Badge variant="primary">PDF Overlay POC</Badge>
          <span className="pdf-overlay-designer__template-name">
            {templateItem?.value || templateItem?.title || 'New PDF template'}
          </span>
          <Button
            size="sm"
            variant="outline-primary"
            className="ml-1"
            title="เลือก PDF พื้นหลังเพื่อเปิด Properties และอัปโหลดไฟล์ใหม่"
            disabled={!templateItem}
            onClick={(event) => {
              event.stopPropagation()
              printAreaProps.onItemClick?.(templateItem, event)
            }}
          >
            <i className="far fa-file-pdf mr-50" />
            จัดการ PDF
          </Button>
        </div>
        <div className="pdf-overlay-designer__metadata">
          {pageView ? (
            <>
              <span>
                Page {pageView.page}/{templateMetadata?.pageCount || 1}
              </span>
              <span>
                {Math.round(pageView.width)} × {Math.round(pageView.height)} px
              </span>
              <span>Rotation {pageView.rotation}°</span>
              <span>User unit {pageView.userUnit}</span>
            </>
          ) : (
            <span>Coordinate preview: PDF viewport 100%</span>
          )}
        </div>
      </div>

      <div className="pdf-overlay-designer__hint">
        PDF ถูกล็อกเป็น background และตำแหน่ง element ถูกบันทึกใน PDF user
        space เพื่อให้ output ตรงกับ Designer หากต้องการเปลี่ยนไฟล์ให้กด
        “จัดการ PDF”
      </div>
      {incompatibleItemIds.length > 0 && (
        <div className="pdf-overlay-designer__hint is-error">
          พบ {incompatibleItemIds.length} element ที่อ้างอิง PDF template
          คนละ revision กรุณาใช้ template เดิมหรือวาง element ใหม่
        </div>
      )}
      {richTextWarningCount > 0 && (
        <div className="pdf-overlay-designer__hint is-error">
          TinyMCE ใน PDF Overlay รองรับข้อความ, ย่อหน้า, ขึ้นบรรทัดใหม่ และ
          datasource token; พบ {richTextWarningCount} element ที่มี table/media
          ซึ่งจะออกเป็นข้อความแบบ plain text
        </div>
      )}

      <div className="pdf-overlay-designer__viewport">
        <PrintArea
          {...printAreaProps}
          page={page}
          pageNo={page}
          boxes={templateSource ? renderedBoxes : boxes}
          layout={pageLayout}
          className="pdf-overlay-designer__page"
          backgroundContent={backgroundContent}
        />
      </div>
    </section>
  )
}

export default PdfOverlayDesigner
